import { Chart } from '@/contexts/AdminContext/dashboardProvider';
import dayjs from 'dayjs';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import React from 'react';
import styles from './index.module.scss';
import { extractNumber, sanitizeSankeyData } from './utils/dashboardUtils';

interface ChartRendererProps {
    chart: Chart;
    colSpan?: number;
    chartsReady: boolean;
}

export const ChartRenderer: React.FC<ChartRendererProps> = ({
    chart, colSpan = 6, chartsReady 
}) => {
    if (typeof chart.data === 'string') {
        return <div className={styles.chartData}>{chart.data}</div>;
    }

    if (!chart.data) {
        return <div className={styles.chartData}>Data not available</div>;
    }

    const chartData = Array.isArray(chart.data) ? chart.data : [];
    
    // Estimate chart width based on layout
    // Approximate width: assuming standard desktop ~1200px+ container, 12 cols. ~100px per col.
    const estimatedWidth = colSpan * 100; 
    const dataCount = chartData.length;
    // Calculate adaptive label width (allow some padding)
    const adaptiveLabelWidth = dataCount > 0 
        ? Math.floor(estimatedWidth / dataCount * 0.9) 
        : 70;
    // Ensure a minimum reasonable width (e.g. 20px) to avoid rendering just "..."
    const finalLabelWidth = Math.max(20, adaptiveLabelWidth);

    // Helper function to format a value with thousand separator
    const formatNumber = (num: number): string => {
        return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    // New Chart Config Logic (ECharts)
    if (chart.chart_config) {
        const config = chart.chart_config;
        let option: EChartsOption = {
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#fff',
                borderColor: '#E5E7EB',
                textStyle: {color: '#1f2937'},
                confine: true,
                extraCssText: 'max-width: 300px; white-space: normal; word-break: break-word;',
                formatter: (params: any) => {
                    // Helper function to format timestamp to date string
                    const formatTimestamp = (val: any): string => {
                        if (typeof val === 'number' && val > 1000000000000) { // Timestamp check
                            return dayjs(val).format('YYYY-MM-DD');
                        }
                        return String(val);
                    };

                    // Helper function to get encode config from series
                    const getSeriesEncode = (seriesIndex: number): any => {
                        if (config.cartesian?.series && config.cartesian.series[seriesIndex]) {
                            return config.cartesian.series[seriesIndex].encode;
                        }
                        if (config.pie?.series && config.pie.series[seriesIndex]) {
                            return config.pie.series[seriesIndex].encode;
                        }
                        if (config.radar?.series && config.radar.series[seriesIndex]) {
                            return config.radar.series[seriesIndex].encode;
                        }
                        if (config.funnel?.series && config.funnel.series[seriesIndex]) {
                            return config.funnel.series[seriesIndex].encode;
                        }
                        if (config.gauge?.series && config.gauge.series[seriesIndex]) {
                            return config.gauge.series[seriesIndex].encode;
                        }
                        return null;
                    };

                    // Helper function to format a value
                    const formatValue = (val: any, seriesIndex: number): string => {
                        const encode = getSeriesEncode(seriesIndex);

                        // Handle array format from dataset.encode: [x, y, ...]
                        if (Array.isArray(val)) {
                            // For typical [x, y] encoding, use the y value (index 1)
                            const arrayVal = val.length > 1 ? val[1] : val[0];
                            if (typeof arrayVal === 'number') {
                                return formatNumber(arrayVal);
                            }
                            // Try to parse if it's a string number
                            if (typeof arrayVal === 'string') {
                                const parsed = parseFloat(arrayVal);
                                if (!isNaN(parsed)) {
                                    return formatNumber(parsed);
                                }
                            }
                            return String(arrayVal);
                        }

                        // If it's already a number, format it
                        if (typeof val === 'number') {
                            return formatNumber(val);
                        }

                        // If it's an object, use encode to find the right field
                        if (typeof val === 'object' && val !== null && encode) {
                            // Try encode.y (for cartesian) or encode.value (for pie/funnel/etc.)
                            const valueField = encode.y || encode.value;
                            if (valueField && val[valueField] !== undefined) {
                                const numericVal = val[valueField];
                                if (typeof numericVal === 'number') {
                                    return formatNumber(numericVal);
                                }
                                const parsed = parseFloat(numericVal);
                                if (!isNaN(parsed)) {
                                    return formatNumber(parsed);
                                }
                            }
                        }

                        // Fallback: try to find a numeric value in the object
                        if (typeof val === 'object' && val !== null) {
                            const numericValue = Object.values(val).find((v: any) => typeof v === 'number');
                            if (typeof numericValue === 'number') {
                                return formatNumber(numericValue);
                            }
                        }

                        // Fallback to string conversion
                        return String(val);
                    };

                    // Handle axis tooltip (line, bar, etc.)
                    if (Array.isArray(params)) {
                        let result = `${formatTimestamp(params[0].axisValue)}<br/>`;
                        params.forEach((param: any) => {
                            result += `${param.marker}${param.seriesName}: ${formatValue(param.value, param.seriesIndex)}<br/>`;
                        });
                        return result;
                    }
                    // Handle item tooltip (pie, scatter, heatmap, etc.)
                    else {
                        // Special handling for pie chart to show percentage
                        if (params.componentSubType === 'pie') {
                            return `${params.marker}${params.name}: ${formatValue(params.value, params.seriesIndex)} (${params.percent}%)`;
                        }

                        // Special handling for heatmap to show all dimensions
                        if (params.componentSubType === 'heatmap' && params.data && typeof params.data === 'object') {
                            const encode = getSeriesEncode(params.seriesIndex);
                            const xField = encode?.x;
                            const yField = encode?.y;
                            const valueField = encode?.value;
                            const xAxisConfig = config.cartesian?.xAxis;
                            const yAxisConfig = config.cartesian?.yAxis;

                            let result = '';
                            if (xField && params.data[xField] !== undefined) {
                                result += `${xAxisConfig?.name || xField}: ${params.data[xField]}<br/>`;
                            }
                            if (yField && params.data[yField] !== undefined) {
                                const singleYAxis = Array.isArray(yAxisConfig)
                                    ? yAxisConfig[0]
                                    : yAxisConfig;
                                const yAxisName = singleYAxis?.name;
                                result += `${yAxisName || yField}: ${params.data[yField]}<br/>`;
                            }
                            if (valueField && params.data[valueField] !== undefined) {
                                const val = params.data[valueField];
                                const formattedVal = typeof val === 'number' ? formatNumber(val) : val;
                                result += `${params.marker}${params.seriesName}: ${formattedVal}`;
                            }
                            return result;
                        }

                        return `${params.marker}${params.seriesName || params.name}: ${formatValue(params.value, params.seriesIndex)}`;
                    }
                }
            },
            legend: {
                top: undefined,
                bottom: 0,
                icon: 'circle',
                formatter: (name: string) => name.length > 20 ? name.slice(0, 20) + '...' : name,
                tooltip: {show: true}
            },
            dataset: {source: chartData},
            grid: {
                left: chartData.length > 0 ? '3%' : '8%',
                right: '8%',
                bottom: '12%',
                containLabel: true
            }
        };

        // 1. Handle Cartesian Charts (Line, Bar, Scatter, Heatmap)
        if (config.cartesian) {
            const {
                xAxis, yAxis, series 
            } = config.cartesian;

            // Dynamic adjustment for multi-line legend
            const legendCount = (series || []).length;
            const isMultiLine = legendCount > 5;
            const gridBottom = isMultiLine ? '20%' : '12%';
            option.grid.bottom = gridBottom;
            
            option.tooltip.trigger = (series || []).some((s: any) => s.type === 'scatter' || s.type === 'heatmap') ? 'item' : 'axis';

            // Check if this is a heatmap chart (both axes should be category)
            const isHeatmap = (series || []).some((s: any) => s.type === 'heatmap');

            // Detect if data is date type and use ECharts built-in time axis
            // Skip date detection for heatmaps to preserve category axis
            // Get the x-axis field from encode configuration
            const firstSeries = (series || []).find((s: any) => s.encode?.x);
            const xAxisKey = firstSeries?.encode?.x || (Array.isArray(chartData) && chartData.length > 0 ? Object.keys(chartData[0])[0] : '');
            const firstDataValue = Array.isArray(chartData) && chartData.length > 0 && xAxisKey ? chartData[0][xAxisKey] : '';

            // Enhanced date detection for multiple formats
            const isDateType = !isHeatmap && firstDataValue && (
                // Check for Date object
                Object.prototype.toString.call(firstDataValue) === '[object Date]'
                // Check for timestamp (number)
                || (typeof firstDataValue === 'number' && firstDataValue > 1000000000000)
                // Check for string date formats
                || (typeof firstDataValue === 'string' && (
                    /^\d{4}-\d{2}-\d{2}/.test(firstDataValue) // ISO date: 2025-12-05
                    || /^\d{4}\/\d{2}\/\d{2}/.test(firstDataValue) // YYYY/MM/DD
                    || /^\d{2}\/\d{2}\/\d{4}/.test(firstDataValue) // MM/DD/YYYY
                    || /^\d{2}-\d{2}-\d{4}/.test(firstDataValue) // DD-MM-YYYY
                    || !isNaN(Date.parse(firstDataValue)) // Any parseable date string
                ))
            );

            // Determine axis type: prioritize explicit config, fallback to 'category'
            const finalType = 'category';

            // Determine interval strategy
            // Use 'auto' if:
            // 1. It's a time axis (continuous)
            // 2. Data is detected as dates (even if using category axis, we want to skip dates rather than truncate)
            // 3. Explicitly requested 'auto'
            const shouldUseAutoInterval =  'auto';

            option.xAxis = {
                type: finalType,
                name: xAxis?.name ? `\n${xAxis.name}` : undefined,
                nameGap: 25,
                nameTextStyle: {
                    color: '#6B7280',
                    verticalAlign: 'top',
                    align: 'right',
                    padding: [10, 10, 0, 0]
                },
                axisLine: {lineStyle: {color: '#E5E7EB'}},
                axisLabel: {
                    color: '#6B7280',
                    interval: shouldUseAutoInterval ? 'auto' : 0,
                    // Only apply truncation logic when we are forcing all labels (interval: 0)
                    // For dates/time, we prefer skipping labels (interval: auto) over truncating text
                    ...(shouldUseAutoInterval ? {} : { width: finalLabelWidth, overflow: 'truncate' }),
                    hideOverlap: true
                },
                axisTick: {show: false}
            };
            
            const yAxisArray = Array.isArray(yAxis) ? yAxis : (yAxis ? [yAxis] : []);

            const yAxisConfigs = yAxisArray.map((y: any, index: number) => {
                return {
                    type: y.type || 'value',
                    name: y.name,
                    position: index > 0 ? 'right' : 'left',
                    offset: index > 1 ? (index - 1) * 60 : 0,
                    axisLine: { show: false },
                    axisTick: { show: false },
                    axisLabel: {
                        color: '#6B7280',
                        formatter: y.axisLabel?.formatter || '{value}',
                        width: 60,
                        overflow: 'truncate',
                        ellipsis: '...',
                        hideOverlap: true
                    },
                    splitLine: {
                        lineStyle: {
                            color: '#E5E7EB',
                            type: 'dashed'
                        },
                        show: index === 0
                    }
                };
            });

            option.yAxis = yAxisConfigs.length > 0 ? yAxisConfigs : [{
                type: 'value',
                axisLine: {show: false},
                axisTick: {show: false},
                axisLabel: {
                    color: '#6B7280',
                    width: 60,
                    overflow: 'truncate',
                    ellipsis: '...',
                    hideOverlap: true
                },
                splitLine: { lineStyle: { color: '#E5E7EB', type: 'dashed' } }
            }];

            const stackLastIndex: Record<string, number> = {};
            (series || []).forEach((s: any, idx: number) => {
                if (s.type === 'bar' && s.stack !== undefined) {
                    stackLastIndex[String(s.stack)] = idx;
                }
            });

            option.series = (series || []).map((s: any, index: number) => {
                const seriesOpt: any = {
                    type: s.type === 'area' ? 'line' : s.type,
                    name: s.name,
                    label: {
                        show: s.type === 'heatmap',
                        formatter: (p: any) => {
                            if (s.encode?.value && p.data && typeof p.data === 'object') {
                                const val = p.data[s.encode.value];
                                const numVal = typeof val === 'number' ? val : parseFloat(val);
                                return isNaN(numVal) ? val : formatNumber(numVal);
                            }
                            return p.value;
                        }
                    },
                    stack: s.stack,
                    encode: s.encode,
                    yAxisIndex: s.yAxisIndex || 0
                };

                if (s.type === 'line' || s.type === 'area') {
                    seriesOpt.smooth = true;
                    seriesOpt.symbol = 'circle';
                    seriesOpt.symbolSize = 6;
                    seriesOpt.itemStyle = {borderWidth: 2};
                    if (s.type === 'area') {
                        seriesOpt.areaStyle = {};
                    }
                } else if (s.type === 'bar') {
                    seriesOpt.barMaxWidth = 40;
                    const isHorizontal = xAxis?.type === 'value';
                    const radius = isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0];
                    if (s.stack !== undefined) {
                        const isLast = stackLastIndex[String(s.stack)] === index;
                        seriesOpt.itemStyle = {borderRadius: isLast ? radius : [0, 0, 0, 0]};
                    } else {
                        seriesOpt.itemStyle = {borderRadius: radius};
                    }
                }

                return seriesOpt;
            });

            // Handle Heatmap VisualMap
            if (series?.some((s: any) => s.type === 'heatmap')) {
                const heatmapSeries = series.find((s: any) => s.type === 'heatmap');
                const valueField = heatmapSeries?.encode?.value;
                let min = 0, max = 100;
                
                if (valueField && typeof valueField === 'string') {
                    const values = chartData.map((d: any) => parseFloat(d[valueField])).filter((v: number) => !isNaN(v));
                    if (values.length > 0) {
                        min = Math.min(...values);
                        max = Math.max(...values);
                    }
                }
                
                if (min === max) {
                    if (max === 0) max = 100;
                    else {
                        min = min - Math.abs(min * 0.1);
                        max = max + Math.abs(max * 0.1);
                    }
                }

                option.visualMap = {
                    min, max,
                    calculable: true,
                    orient: 'horizontal',
                    left: 'center',
                    bottom: '0',
                    dimension: valueField,
                    inRange: {color: ['#f6efa6', '#d88273', '#bf444c']}
                };
                option.grid.bottom = '25%'; // Make room for visualMap
            }
        }

        // 2. Handle Pie Charts
        else if (config.pie) {
            option.tooltip.trigger = 'item';
            option.xAxis = undefined;
            option.yAxis = undefined;
            option.grid = undefined;
            
            // Dynamic adjustment for multi-line legend
            const dataCount = chartData.length;
            // If there are many items, legend might wrap. Move chart up.
            const isMultiLine = dataCount > 5;
            const centerY = isMultiLine ? '40%' : '50%';
            const radiusOuter = isMultiLine ? '60%' : '70%';

            option.series = (config.pie.series || []).map((s: any) => ({
                type: 'pie',
                name: s.name,
                radius: ['40%', radiusOuter],
                center: ['50%', centerY],
                itemStyle: {
                    borderRadius: 10,
                    borderColor: '#fff',
                    borderWidth: 2
                },
                label: {
                    show: false,
                    position: 'center'
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 24,
                        fontWeight: 'bold',
                        formatter: '{b}\n{d}%'
                    }
                },
                encode: {
                    itemName: s.encode?.itemName,
                    value: s.encode?.value
                }
            }));
        }

        // 3. Handle Radar Charts
        else if (config.radar) {
            option.tooltip.trigger = 'item';
            option.xAxis = undefined;
            option.yAxis = undefined;
            
            let radarIndicators = config.radar.indicator || [];

            // Dynamic adjustment for multi-line legend
            const dataCount = chartData.length;
            // If there are many items (polygons), legend might wrap. Move chart up.
            const isMultiLine = dataCount > 5;
            const centerY = isMultiLine ? '45%' : '50%';
            const radius = isMultiLine ? '60%' : '65%';
            
            option.radar = {
                indicator: radarIndicators,
                center: ['50%', centerY],
                radius: radius,
                splitNumber: 5,
                axisName: { color: '#6B7280' },
                splitLine: {lineStyle: { color: ['#E5E7EB'] }},
                splitArea: {
                    show: true,
                    areaStyle: {color: ['rgba(250,250,250,0.3)', 'rgba(200,200,200,0.3)']}
                }
            };

            option.series = (config.radar.series || []).map((s: any) => ({
                type: 'radar',
                name: s.name,
                symbol: 'circle',
                symbolSize: 6,
                areaStyle: { opacity: 0.2 },
                // Radar doesn't use standard encode, it expects data in array of values matching indicators
                // We need to map chartData rows to values array if data is in object format
                data: chartData.map((row: any) => ({
                    value: radarIndicators.map((ind: any) => row[ind.field]),
                    name: s.name // This might be wrong if multiple series. 
                })).map((d: any, i: number) => ({
                    ...d,
                    name: chartData[i][Object.keys(chartData[i])[0]] // Try to find a name for the polygon
                }))
            }));
            
            // Override dataset since we manually constructed series.data
            option.dataset = undefined;
        }

        // 4. Handle Funnel Charts
        else if (config.funnel) {
            option.tooltip.trigger = 'item';
            option.xAxis = undefined;
            option.yAxis = undefined;

            // Dynamic adjustment for multi-line legend
            const dataCount = chartData.length;
            const isMultiLine = dataCount > 5;
            const bottom = isMultiLine ? 100 : 60;
            const top = isMultiLine ? 40 : 60;

            option.series = (config.funnel.series || []).map((s: any) => {
                // Calculate max value dynamically
                let max = 100;
                const valueField = s.encode?.value;
                let values: number[] = [];

                if (valueField) {
                    values = chartData.map((d: any) => {
                        const val = extractNumber(d[valueField]);
                        return typeof val === 'number' ? val : 0;
                    });
                    
                    // Use the first value as the base (100%)
                    if (values.length > 0) {
                        max = values[0];
                    }
                }
                // Ensure max is at least something positive
                if (max === 0) max = 100;

                return {
                    type: 'funnel',
                    name: s.name,
                    left: '10%',
                    top: top,
                    bottom: bottom,
                    width: '80%',
                    min: 0,
                    max: max,
                    minSize: '0%',
                    maxSize: '100%',
                    gap: 2,
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: (params: any) => {
                            const valueField = s.encode?.value;
                            let val = params.value;
                            if (valueField && params.data && typeof params.data === 'object') {
                                val = params.data[valueField];
                            }
                            const formattedVal = typeof val === 'number' ? formatNumber(val) : val;
                            
                            // Calculate conversion rates
                            let ratesStr = '';
                            const currentNum = values[params.dataIndex];
                            
                            if (typeof currentNum === 'number') {
                                // 1. Overall Conversion (vs First Step)
                                const firstNum = values[0];
                                if (firstNum > 0) {
                                    const overallRate = ((currentNum / firstNum) * 100).toFixed(2);
                                    ratesStr += `\n${overallRate}%`;
                                }
                            }

                            return `${params.name}:${formattedVal}${ratesStr}`;
                        }
                    },
                    itemStyle: {
                        borderColor: '#fff',
                        borderWidth: 1
                    },
                    emphasis: {label: {fontSize: 20}},
                    encode: {
                        itemName: s.encode?.itemName,
                        value: s.encode?.value
                    }
                };
            });
        }

        // 5. Handle Gauge Charts
        else if (config.gauge) {
            option.tooltip.trigger = 'item';
            option.xAxis = undefined;
            option.yAxis = undefined;
            
            option.series = (config.gauge.series || []).map((s: any) => ({
                type: 'gauge',
                name: s.name,
                startAngle: 180,
                endAngle: 0,
                center: ['50%', '75%'],
                radius: '90%',
                min: 0,
                max: 100,
                splitNumber: 8,
                axisLine: {
                    lineStyle: {
                        width: 6,
                        color: [
                            [0.25, '#FF6E76'],
                            [0.5, '#FDDD60'],
                            [0.75, '#58D9F9'],
                            [1, '#7CFFB2']
                        ]
                    }
                },
                pointer: {
                    icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
                    length: '12%',
                    width: 20,
                    offsetCenter: [0, '-60%'],
                    itemStyle: {color: 'auto'}
                },
                axisTick: {
                    length: 12,
                    lineStyle: {
                        color: 'auto',
                        width: 2
                    }
                },
                splitLine: {
                    length: 20,
                    lineStyle: {
                        color: 'auto',
                        width: 5
                    }
                },
                axisLabel: {
                    color: '#464646',
                    fontSize: 20,
                    distance: -60,
                    rotate: 'tangential',
                    formatter: function (value: number) {
                        if (value === 87.5) {
                            return 'A';
                        } else if (value === 62.5) {
                            return 'B';
                        } else if (value === 37.5) {
                            return 'C';
                        } else if (value === 12.5) {
                            return 'D';
                        }
                        return '';
                    }
                },
                title: {
                    offsetCenter: [0, '-10%'],
                    fontSize: 20
                },
                detail: {
                    fontSize: 30,
                    offsetCenter: [0, '-35%'],
                    valueAnimation: true,
                    formatter: function (value: number) {
                        return formatNumber(value);
                    },
                    color: 'auto'
                },
                data: chartData.map((row: any) => ({
                    value: row[s.encode?.value || 'value'],
                    name: s.name
                }))
            }));
            
            option.dataset = undefined;
        }

        // 6. Handle Sankey Charts
        else if (config.sankey) {
            option.tooltip.trigger = 'item';
            option.xAxis = undefined;
            option.yAxis = undefined;

            option.series = (config.sankey.series || []).map((s: any) => {
                const rawLinks = chartData.map((d: any) => ({
                    source: d[s.encode?.source],
                    target: d[s.encode?.target],
                    value: d[s.encode?.value]
                }));
                const links = sanitizeSankeyData(rawLinks);

                // Re-calculate nodes based on sanitized links to avoid orphaned nodes
                const relevantNodes = new Set([
                    ...links.map((l: any) => l.source),
                    ...links.map((l: any) => l.target)
                ]);

                return {
                    type: 'sankey',
                    layout: 'none',
                    emphasis: { focus: 'adjacency' },
                    data: Array.from(relevantNodes).map(name => ({ name })),
                    links
                };
            });
            
            option.dataset = undefined;
        }

        if (!chartsReady) {
            return <div style={{ height: '100%', width: '100%' }} />;
        }
        return (
            <ReactECharts
                option={option}
                style={{ height: '100%', width: '100%' }}
                notMerge={true}
                lazyUpdate={true}
            />
        );
    }

    return <div className={styles.chartData}>Unknown chart config</div>;
};
